import type { SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Modal } from '../../Modal';
import { Button, ScrollArea, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { SocialPostPreviewCard } from '../chat-surface/MessageRow/SocialPostPreviewCard';
import { ChoiceChips } from '../chat-surface/MessageRow/ChoiceChips';
import { FACEBOOK_SAMPLES } from './dev-facebook-post-samples';
import { INSTAGRAM_SAMPLES } from './dev-instagram-post-samples';
import { LINKEDIN_SAMPLES, X_SAMPLES } from './dev-linkedin-x-post-samples';
import { TIKTOK_YOUTUBE_SAMPLES } from './dev-tiktok-youtube-post-samples';
import { PINTEREST_GBP_SAMPLES } from './dev-pinterest-gbp-post-samples';

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const PHOTO_A = 'https://static.wixstatic.com/media/11062b_7e0c4e4f2b2e4b9a8f3f2b1c5d6e7f80~mv2.jpg';
const PHOTO_B = 'https://static.wixstatic.com/media/11062b_2a3b4c5d6e7f4a8b9c0d1e2f3a4b5c6d~mv2.jpg';
const VIDEO = 'https://video.wixstatic.com/video/11062b_0a1b2c3d4e5f4a6b8c9d0e1f2a3b4c5d/720p/mp4/file.mp4';

const ACTIONS = ['Post it', 'Edit first', 'Drop it'];

const SAMPLES: { title: string; card: SocialPostPreviewCardMetadata }[] = [
  {
    title: 'Facebook Page: two photos, link, goes out now',
    card: {
      network: 'facebook',
      networkLabel: 'Facebook',
      accountName: "Joe's Bakery",
      accountLabel: "Joe's Bakery (Facebook Page)",
      caption: 'Fresh sourdough every Friday.\nCome early, it sells out by noon! #Sourdough #FreshBread',
      media: [
        { url: PHOTO_A, kind: 'image' },
        { url: PHOTO_B, kind: 'image' },
      ],
      mediaCount: 2,
      link: 'https://joes.example/menu',
      decisionId: 'd-1',
      taskId: 't-1',
      labels: LABELS,
    },
  },
  {
    title: 'Instagram: one video, scheduled, container expires',
    card: {
      network: 'instagram',
      networkLabel: 'Instagram',
      postType: 'reel',
      accountLabel: '@joesbakery',
      caption: 'Behind the scenes: shaping 200 loaves before sunrise.',
      media: [{ url: VIDEO, kind: 'video' }],
      mediaCount: 1,
      scheduledAt: '2026-10-30T07:30:00.000Z',
      expiresWithinDay: true,
      decisionId: 'd-2',
      taskId: 't-2',
      labels: { ...LABELS, postType: 'Reel', expires: 'Instagram keeps a prepared post for about a day. Approve before then.' },
    },
  },
  {
    title: 'LinkedIn: six items, two not shown, long caption',
    card: {
      network: 'linkedin',
      networkLabel: 'LinkedIn',
      postType: 'carousel',
      accountLabel: "Joe's Bakery",
      caption:
        'We turned ten this week.\n\nTen years ago Joe opened the door at 5am with one oven and a borrowed mixer.\nToday we bake for three neighbourhoods, two schools and a hospital.\nThank you to every regular who came back.\nThank you to the team who shows up before the sun does.\nAnd thank you to the oven, which has been repaired eleven times.\nHere is to the next ten.',
      media: [
        { url: PHOTO_A, kind: 'image' },
        { url: PHOTO_B, kind: 'image' },
        { url: VIDEO, kind: 'video' },
        { url: PHOTO_A, kind: 'image' },
        { url: PHOTO_B, kind: 'image' },
        { url: PHOTO_A, kind: 'image' },
      ],
      mediaCount: 8,
      scheduledText: 'next Tuesday morning',
      decisionId: 'd-3',
      taskId: 't-3',
      labels: { ...LABELS, postType: 'Carousel', mediaNotShown: 'Photos or videos not shown here: 2' },
    },
  },
  {
    title: 'X: text only',
    card: {
      network: 'x',
      networkLabel: 'X',
      caption: 'Open late tonight for the street fair. Cinnamon buns until 10pm.',
      media: [],
      mediaCount: 0,
      decisionId: 'd-4',
      taskId: 't-4',
      labels: LABELS,
    },
  },
  {
    title: 'Google Business Profile: broken image URL',
    card: {
      network: 'google_business',
      networkLabel: 'Google Business Profile',
      accountLabel: "Joe's Bakery, Main St.",
      caption: 'Holiday hours: closed on the 25th, open 7am to 1pm on the 26th.',
      media: [{ url: 'https://static.wixstatic.com/media/expired-signed-url.jpg', kind: 'image' }],
      mediaCount: 1,
      decisionId: 'd-5',
      taskId: 't-5',
      labels: LABELS,
    },
  },
  {
    title: 'Mastodon: unknown network, Hebrew caption',
    card: {
      network: 'mastodon',
      networkLabel: 'Mastodon',
      accountLabel: '@joes@bakers.social',
      caption: 'לחם מחמצת טרי בכל יום שישי. בואו מוקדם!',
      media: [
        { url: PHOTO_A, kind: 'image' },
        { url: PHOTO_B, kind: 'image' },
        { url: VIDEO, kind: 'video' },
      ],
      mediaCount: 3,
      decisionId: 'd-6',
      taskId: 't-6',
      labels: LABELS,
    },
  },
  {
    title: 'Pinterest pin: no account name, one item shown of three',
    card: {
      network: 'pinterest',
      networkLabel: 'Pinterest',
      postType: 'pin',
      caption: 'Five-minute desk stretches you can do between meetings.',
      media: [{ url: PHOTO_B, kind: 'image' }],
      mediaCount: 3,
      link: 'https://joes.example/stretches',
      decisionId: 'd-7',
      taskId: 't-7',
      labels: { ...LABELS, postType: 'Pin', mediaNotShown: 'Photos or videos not shown here: 2' },
    },
  },
];

const EMPTY = new Set<string>();

export function DevSocialPostPreviewPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <SocialPostPreviewPanel {...props} />;
}

function SocialPostPreviewPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} width={640} title="Social post preview cards" footer={<Button onClick={onClose}>Done</Button>}>
      <Typography variant="p3" color="secondary">
        The real card, fed the shape the harness emits when it holds a public post for approval: two photos, a single video, a grid with +N, text
        only, a broken image, an unknown network, a post with no account name. The chips under each are the reply&apos;s own choice chips, inert here.
      </Typography>
      <ScrollArea style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16, maxHeight: '70vh' }}>
        {[
          ...FACEBOOK_SAMPLES,
          ...INSTAGRAM_SAMPLES,
          ...LINKEDIN_SAMPLES,
          ...X_SAMPLES,
          ...TIKTOK_YOUTUBE_SAMPLES,
          ...PINTEREST_GBP_SAMPLES,
          ...SAMPLES.map((sample) => ({ id: sample.title, ...sample })),
        ].map((sample) => (
          <section key={sample.id} data-sample={sample.id}>
            <Typography variant="p3" color="secondary" component="p" style={{ margin: '0 0 6px' }}>
              {sample.title}
            </Typography>
            <SocialPostPreviewCard card={sample.card} />
            <ChoiceChips choices={{ askId: sample.title, options: ACTIONS.map((label, i) => ({ id: `opt_${i + 1}`, label })) }} selectedIds={EMPTY} />
          </section>
        ))}
      </ScrollArea>
    </Modal>
  );
}
